import React, { useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { Button, Form, Input, InputGroup, Label, Table } from "reactstrap";
import { PaginationControl } from "react-bootstrap-pagination-control";
import { t } from "i18next";

import { PAGE_SIZE_OPTIONS } from "../../constants";
import EmployeeService from "../../services/employee";

import SortableHeader from "../../components/SortableHeader";
import NoRecords from "../../components/Table/NoRecords";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

import useListPage from "../../hooks/useListPage";

const HeaderList = [
  {
    title: '',  // Edit button column (RIGHT side in RTL)
    value: 'editAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
  {
    title: t("FULL_NAME"),
    value: "fullName",
    style: { minWidth: "150px", width: "30%" },
  },
  {
    title: t("EMAIL_LABEL"),
    value: "mail",
    style: { minWidth: "200px", width: "30%" },
  },
  {
    title: t("MOBILE_NO_LABEL"),
    value: "phoneNumber",
    style: { minWidth: "150px", width: "30%" },
  },
  {
    title: '',  // Delete button column (LEFT side in RTL)
    value: 'deleteAction',
    style: { minWidth: '70px', width: '70px' },
    sortable: false,
  },
];

function EmployeeList() {
  const navigate = useNavigate();
  const [setPageTitle, setBreadcrumbItems] = useOutletContext();

  const {
    list,
    isLoading,
    totalCount,
    currentPage,
    itemsPerPage,
    deleteModal,
    debouncedSearch,
    sortBy,
    handlePageClick,
    handlePageSizeChange,
    openDeleteModal,
    closeDeleteModal,
    confirmDelete,
  } = useListPage({
    service: EmployeeService,
    listMethod: "listEmployee",
    deleteMethod: "deleteEmployee",
    initialSort: { value: 'fullName', asc: true },
    initialPageSize: 10,
    deleteIdField: "id",
  });

  useEffect(() => {
    setPageTitle(t("EMPLOYEE.TITLE"));
    setBreadcrumbItems([
      { title: t("APPLICATION_TITLE"), link: "#" },
      { title: t("EMPLOYEE.TITLE"), link: "#" },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editFn = (item) => {
    const fullName = `${item.firstName} ${item.lastName}`;
    navigate(`../update/${item.id}`, {
      state: { fullName, companyId: item.companyId },
    });
  };

  return (
    <React.Fragment>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Form className="search-filter" onSubmit={(e) => e.preventDefault()}>
          <InputGroup>
            <Input
              onChange={debouncedSearch}
              placeholder={t("SEARCH")}
            />
            <Button color="primary" type="button">
              <i className="ri-search-line"></i>
            </Button>
          </InputGroup>
        </Form>
      </div>

      <Table striped bordered responsive size="sm">
        <thead>
          <tr>
            <SortableHeader
              headers={HeaderList}
              isDisabled={list.length === 0}
              sortClick={sortBy}
              showActionCol={false}
            />
          </tr>
        </thead>

        <tbody>
          {list.map((item, l0Idx) => (
            <tr key={l0Idx}>
              {/* Edit button - RIGHT side in RTL */}
              <td className="text-center">
                <Button color="primary" size="sm" title={t('EMPLOYEE.UPDATE')} onClick={() => editFn(item)}>
                  {t('EMPLOYEE.EDIT_BTN') || t('EDIT') || 'שנה'}
                </Button>
              </td>
              <td>{item.firstName + " " + item.lastName}</td>
              <td>{item.mail}</td>
              <td>{item.phoneNumber}</td>
              {/* Delete button - LEFT side in RTL */}
              <td className="text-center">
                <Button color="danger" size="sm" title={t('EMPLOYEE.DELETE')} onClick={() => openDeleteModal(item)}>
                  {t('EMPLOYEE.DELETE_BTN') || 'לבטל'}
                </Button>
              </td>
            </tr>
          ))}

          {!isLoading && list.length === 0 && (
            <NoRecords colSpan={HeaderList.length} />
          )}
        </tbody>
      </Table>

      {/* Pagination and Rows per page */}
      <div className="custom-pagination mt-2 d-flex justify-content-between align-items-center flex-row-reverse">
        {/* Rows per page - RIGHT side in RTL */}
        <div className="d-flex align-items-center gap-2">
          <Label className="mb-0">{t('ROWS_PER_PAGE') || 'שורות לדף'}:</Label>
          <Input
            type="select"
            value={itemsPerPage}
            onChange={handlePageSizeChange}
            style={{ width: '80px' }}
            bsSize="sm"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Input>
        </div>

        {/* Pagination - LEFT side in RTL */}
        <PaginationControl
          page={currentPage}
          total={totalCount}
          limit={itemsPerPage}
          changePage={handlePageClick}
          next={true}
          last={true}
          ellipsis={1}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={deleteModal.show}
        isLoading={isLoading}
        confirmFn={confirmDelete}
        cancelFn={closeDeleteModal}
      />
    </React.Fragment>
  );
}

export default EmployeeList;
